import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Modal from "react-modal";
import ReactDropzone from "react-dropzone";

import { TTP_API_URL } from "Config";
import { addLandaSize } from "Utils";
import { resetLogoSelector, setLogoSelector } from "Actions";

import IconClose from "../../Icons/IconClose";
import IconFolder from "../../Icons/IconFolder";
import IconUpload from "../../Icons/IconUpload";
import IconCheck from "../../Icons/IconCheck";
import IconArrowLeft from "../../Icons/IconArrowLeft";

import { fetchMedias, uploadMediaFiles } from "Thunks";
import MultiSwitchComponent from "Common/Switch/MultiSwitch/MultiSwitchComponent";

import styles from "./LogoSelector.local.scss";
import _ from "i18n";

export class LogoSelector extends Component {
  state = {
    openPreview: false,
    newSelectedLogo: null,
    newSelectedSize: null,
    newSelectedPosition: null
  };

  static propTypes = {
    loggedAs: PropTypes.string,
    isOpen: PropTypes.bool
  };

  fetchItems = () => {
    const { fetchMedias } = this.props;
    const cols = 4;
    const rows = 4;

    fetchMedias({ type: "LOGO", limit: rows * cols - 1 });
  };

  afterOpenModal = () => {
    const { selectedLogo, selectedSize, selectedPosition } = this.props;
    this.setState({
      newSelectedLogo: selectedLogo,
      newSelectedSize: selectedSize,
      newSelectedPosition: selectedPosition
    });
    this.fetchItems();
  };

  closeModal = () => {
    this.setState({
      openPreview: false,
      newSelectedLogo: null,
      newSelectedSize: null,
      newSelectedPosition: null
    });
    this.props.setLogoSelector({ isOpen: false });
  };

  applyLogo = () => {
    const {
      newSelectedLogo,
      newSelectedSize,
      newSelectedPosition
    } = this.state;
    this.props.setLogoSelector({
      selectedLogo: newSelectedLogo,
      selectedSize: newSelectedSize,
      selectedPosition: newSelectedPosition
    });
    this.closeModal();
  };

  toggleSelectedLogo = logo => {
    const { newSelectedLogo } = this.state;

    if (logo && newSelectedLogo && newSelectedLogo.id === logo.id) {
      this.setState({ newSelectedLogo: null });
      return;
    }

    this.setState({ newSelectedLogo: logo, openPreview: true });
  };

  selectPosition = (i, j) => {
    const { newSelectedPosition, newSelectedLogo } = this.state;
    if (
      newSelectedPosition &&
      newSelectedPosition.i === i &&
      newSelectedPosition.j === j
    ) {
      return;
    }

    this.setState({ newSelectedPosition: { i, j } });

    if (newSelectedLogo) {
      this.setState({ openPreview: true });
    }
  };

  selectSize = newSelectedSize => {
    if (newSelectedSize === this.state.newSelectedSize) {
      return;
    }
    this.setState({ newSelectedSize });

    if (this.state.newSelectedLogo) {
      this.setState({ openPreview: true });
    }
  };

  renderFetchingState() {
    return [...Array(11).keys()].map(key => (
      <div key={key} className={`cell ${styles.listItem}`}>
        <div className={`${styles.logo} ${styles.fetching}`} />
      </div>
    ));
  }

  renderLogos() {
    const { newSelectedLogo } = this.state;
    const { logos, fetching, error } = this.props;

    let fetchingState = "none";

    if (fetching) {
      fetchingState = "fetching";
    } else if (error && error.code !== 404) {
      fetchingState = "error";
    } else if (error && error.code === 404) {
      fetchingState = "success"; // This an empty response (not an error for lists)
    } else {
      fetchingState = "success";
    }

    if (fetchingState === "none") {
      return null; // First render (before any request start )
    } else if (fetchingState === "fetching") {
      return this.renderFetchingState();
    } else if (fetchingState === "error") {
      return null; // RENDER ERROR COMPONENT
    } else if (logos.length === 0) {
      return null;
    }

    return logos.map(logo => (
      <div
        key={logo.id}
        className={`cell ${styles.listItem} ${
          newSelectedLogo && newSelectedLogo.id === logo.id
            ? styles.logoSelected
            : ""
        }`}
      >
        <div
          className={styles.logo}
          onClick={() => this.toggleSelectedLogo(logo)}
        >
          <div className={styles.logoImgContainer}>
            <img
              src={
                logo.fullMediaUrl
                  ? addLandaSize(logo.fullMediaUrl, 600)
                  : TTP_API_URL + "/" + logo.webPath
              }
              alt="logo"
            />
          </div>
          {newSelectedLogo &&
            newSelectedLogo.id === logo.id && (
              <div className={styles.logoSelectedIcon}>
                <IconCheck size={10} />
              </div>
            )}
        </div>
      </div>
    ));
  }

  onDropLogo = (acceptedFiles, rejectedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) {
      return;
    }

    // TODO rejectedFiles ?

    this.props.uploadMediaFiles(acceptedFiles, "LOGO");
  };

  renderAddLogo() {
    return (
      <div className={`cell ${styles.listItem}`}>
        <ReactDropzone
          accept={["image/*"]}
          onDrop={this.onDropLogo}
          className={styles.dropzone}
          activeClassName={styles.dropzoneActive}
          rejectClassName={styles.dropzoneReject}
        >
          <IconUpload size={50} />
        </ReactDropzone>
      </div>
    );
  }

  renderUploadingLogos() {
    const { uploadingLogos } = this.props;

    if (uploadingLogos.length === 0) {
      return null;
    }

    return uploadingLogos.map(({ id, progress, file }) => (
      <div key={id} className={`cell ${styles.listItem}`}>
        <div className={`${styles.logo} ${styles.uploadingLogo}`}>
          <div className={styles.logoImgContainer}>
            <img src={URL.createObjectURL(file)} alt="logo" />
          </div>
          <div className={styles.uploadingLogoOverlay} />
          <div
            className="progress"
            role="progressbar"
            tabIndex="0"
            aria-valuenow={progress}
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <span
              className="progress-meter"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    ));
  }

  render() {
    const {
      openPreview,
      newSelectedLogo,
      newSelectedSize,
      newSelectedPosition
    } = this.state;
    const { isOpen } = this.props;

    const applyPreviewStyles = {
      position: "relative",
      width: "100%",
      height: "100%",
      display: "flex",
      flexWrap: "wrap"
    };

    const applyGridStyles = {
      ...applyPreviewStyles,
      height: `175px`,
      margin: "24px 10px 1rem 10px",
      border: "1px solid #DBE0E5",
      width: "calc(100% - 20px)"
    };

    const applyCellStyles = {
      border: "1px solid #DBE0E5",
      width: "25%",
      height: "25%"
    };

    const circleWith = 20;
    const applyCircleStyles = {
      position: "absolute",
      backgroundColor: "#DBE0E5",
      width: `${circleWith}px`,
      height: `${circleWith}px`,
      borderRadius: "50%",
      cursor: "pointer"
    };

    let si = (newSelectedPosition && newSelectedPosition.i) || 0;
    let sj = (newSelectedPosition && newSelectedPosition.j) || 0;

    //à mettre dans la config
    const previewHeight = 491;
    const previewWidth = 785;
    const nbAreas = 5;

    let previewLogoStyles = null;

    if (newSelectedLogo) {
      si = newSelectedPosition.i;
      sj = newSelectedPosition.j;

      const logoWHCoefficient =
        parseFloat(newSelectedLogo.meta.width) /
        parseFloat(newSelectedLogo.meta.height);

      let percent = 20;

      switch (newSelectedSize) {
        case "S":
          percent = 10;
          break;
        case "M":
          percent = 20;
          break;
        case "L":
          percent = 30;
          break;
        case "XL":
          percent = 50;
          break;
        default:
          percent = 20;
      }

      let previewReducedHeight = (previewHeight * percent) / 100;
      let previewReducedWidth = (previewWidth * percent) / 100;

      let newLogoWidth = previewReducedWidth;
      let newLogoHeight = newLogoWidth / logoWHCoefficient;

      if (newLogoHeight > previewReducedHeight) {
        newLogoHeight = previewReducedHeight;
        newLogoWidth = newLogoHeight * logoWHCoefficient;
      }

      const widthStep = Math.round(
        (previewWidth - newLogoWidth) / (nbAreas - 1)
      );
      const heightStep = Math.round(
        (previewHeight - newLogoHeight) / (nbAreas - 1)
      );

      previewLogoStyles = {
        filter: "saturate(120%)",
        position: "absolute",
        width: `${newLogoWidth}px`,
        left: `calc(${si * 25}% ${
          si * 25 === 100 ? "-" + newLogoWidth + "px" : "+ 1px"
        })`
      };

      previewLogoStyles.top = `calc(${sj * heightStep}px + 1px)`;

      const previewLogoMargin = "4px";
      if (sj === 0) {
        previewLogoStyles.marginTop = previewLogoMargin;
      } else if (sj === 4) {
        previewLogoStyles.marginBottom = previewLogoMargin;
      }

      previewLogoStyles.left = `calc(${si * widthStep}px + 1px)`;

      if (si === 0) {
        previewLogoStyles.marginLeft = previewLogoMargin;
      } else if (si === 4) {
        previewLogoStyles.marginRight = previewLogoMargin;
      }
    }

    return (
      <Modal
        isOpen={isOpen}
        onAfterOpen={this.afterOpenModal}
        onRequestClose={this.closeModal}
        className={{
          base: styles.modalContent,
          afterOpen: styles.modalContentAfterOpen,
          beforeClose: styles.modalContentBeforeClose
        }}
        overlayClassName={{
          base: styles.modalOverlay,
          afterOpen: styles.modalOverlayAfterOpen,
          beforeClose: styles.modalOverlayBeforeClose
        }}
        closeTimeoutMS={300}
        contentLabel="Logo selector"
      >
        <div className={styles.modal}>
          <div className={styles.close} onClick={this.closeModal}>
            <IconClose size={14} />
          </div>
          <div className={styles.header}>
            <IconFolder className={styles.headerIcon} size={29} />
            {_("Apply a logo to your medias")}
          </div>
          <div className={styles.body}>
            {!openPreview && (
              <div className={styles.list}>
                <div className={styles.listHeader}>
                  {_("Select or import your logo")}
                </div>
                <div className={`grid-x ${styles.listBody}`}>
                  {this.renderAddLogo()}
                  {this.renderUploadingLogos()}
                  {this.renderLogos()}
                </div>
              </div>
            )}
            {openPreview && (
              <div className={styles.preview}>
                <div
                  style={{
                    position: "absolute",
                    left: "30px",
                    top: "5px",
                    cursor: "pointer"
                  }}
                  onClick={() => this.setState({ openPreview: false })}
                >
                  <IconArrowLeft size={20} />
                </div>
                <div className={styles.previewGrid}>
                  <div
                    className="mask-preview-grid"
                    style={{
                      ...applyPreviewStyles,
                      border: "1px solid #DBE0E5"
                    }}
                  >
                    {[...Array(16).keys()].map(key => (
                      <div key={key} style={applyCellStyles} />
                    ))}

                    {[...Array(25).keys()].map((_, index) => {
                      const i = Math.floor((index * 3) / 15);
                      const j = index - i * 5;
                      const isSelected = si === i && sj === j;
                      return (
                        <div
                          key={`${i},${j}`}
                          className={`mask-apply__circle ${
                            isSelected ? "mask-apply__circle--selected" : ""
                          }`}
                          style={{
                            ...applyCircleStyles,
                            width: "14px",
                            height: "14px",
                            top: `calc(${j * 25}% - 7px)`,
                            left: `calc(${i * 25}% - 7px)`,
                            backgroundColor: isSelected ? "#18A0FB" : "#DBE0E5"
                          }}
                        />
                      );
                    })}

                    {newSelectedLogo && (
                      <img
                        src={
                          newSelectedLogo.fullMediaUrl
                            ? addLandaSize(newSelectedLogo.fullMediaUrl, 600)
                            : `${TTP_API_URL}/${newSelectedLogo.webPath}`
                        }
                        alt=""
                        style={previewLogoStyles}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className={styles.settings}>
              <div className={styles.settingsSection}>
                <div className={styles.settingsSectionTitle}>
                  {_("Select size")}
                </div>
                <MultiSwitchComponent
                  labels={[
                    _("Small"),
                    _("Medium"),
                    _("Large"),
                    _("eXtra Large")
                  ]}
                  vals={["S", "M", "L", "XL"]}
                  selectedOption={newSelectedSize}
                  onChange={e => this.selectSize(e.target.value)}
                />
              </div>
              <div className={styles.settingsSection}>
                <div className={styles.settingsSectionTitle}>
                  {_("Select position")}
                </div>
                <div className="mask-apply-grid" style={applyGridStyles}>
                  {[...Array(16).keys()].map(key => (
                    <div
                      key={key}
                      className="mask-apply__cell"
                      style={applyCellStyles}
                    />
                  ))}
                  {[...Array(25).keys()].map((_, index) => {
                    const i = Math.floor((index * 3) / 15);
                    const j = index - i * 5;
                    const isSelected = si === i && sj === j;
                    return (
                      <div
                        key={`${i},${j}`}
                        className={`mask-apply__circle ${
                          isSelected ? "mask-apply__circle--selected" : ""
                        }`}
                        style={{
                          ...applyCircleStyles,
                          top: `calc(${j * 25}% - ${circleWith / 2}px)`,
                          left: `calc(${i * 25}% - ${circleWith / 2}px)`,
                          backgroundColor: isSelected ? "#18A0FB" : "#DBE0E5"
                        }}
                        onClick={this.selectPosition.bind(this, i, j)}
                      />
                    );
                  })}
                </div>
              </div>
              <div className={styles.actions}>
                <button
                  className={styles.cancelButton}
                  onClick={this.closeModal}
                >
                  {_("Cancel")}
                </button>
                <button className={styles.applyButton} onClick={this.applyLogo}>
                  {_("Apply")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  }
}

// Connected Component
const mapStateToProps = (state, ownProps) => ({
  lng: state.params.lng,
  isOpen: state.logoSelector.isOpen,
  selectedLogo: state.logoSelector.selectedLogo,
  selectedPosition: state.logoSelector.selectedPosition,
  selectedSize: state.logoSelector.selectedSize,
  logos: state.medias.logos.items,
  fetching: state.medias.logos.fetching,
  fetched: state.medias.logos.fetched,
  error: state.medias.logos.items.error,
  uploadingLogos: state.medias.uploading.items
});

const mapDispatchToProps = dispatch => ({
  resetLogoSelector: () => dispatch(resetLogoSelector()),
  setLogoSelector: options => dispatch(setLogoSelector(options)),
  fetchMedias: options => dispatch(fetchMedias(options)),
  uploadMediaFiles: (files, docType) =>
    dispatch(uploadMediaFiles(files, docType))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LogoSelector);



// WEBPACK FOOTER //
// ./src/components/Reveal/LogoSelector/LogoSelector.js